import { Injector } from '@angular/core';
import { TokenStore } from './token-store';
import { AuthSessionService } from './auth-session.service';

/**
 * Access tokens for WebSocket handshakes.
 *
 * Access tokens live 15 minutes (SIMPLE_JWT ACCESS_TOKEN_LIFETIME) and are
 * normally refreshed by the HTTP interceptor when an API call returns 401. A
 * WebSocket handshake never goes through that interceptor, so a socket that
 * (re)connects after the token expired presented a dead token, the backend
 * (utils/ws_auth.py) resolved it to AnonymousUser, the consumer closed before
 * accept(), and the browser reported "Unexpected response code: 403" - in a
 * loop, until some unrelated HTTP call happened to trigger a refresh.
 *
 * Every socket gets its token from here instead of reading TokenStore directly.
 */

/** True if the JWT's `exp` is in the past or within `skewMs` of it. */
export function isJwtExpired(token: string, skewMs = 30000): boolean {
    try {
        const part = token.split('.')[1];
        if (!part) return false; // not a JWT (legacy key) - let the server decide
        const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)));
        if (!payload || typeof payload.exp !== 'number') return false;
        return payload.exp * 1000 <= Date.now() + skewMs;
    } catch (e) {
        return false;
    }
}

/**
 * Resolve a token that is valid right now, refreshing first when needed.
 *
 * `force` refreshes even if the token does not look expired by the local
 * clock (clock skew, a revoked token) - used once after a rejected handshake.
 *
 * Uses AuthSessionService.refresh(), which is single-flight and shared with
 * the HTTP interceptor. Never refresh any other way: with
 * ROTATE_REFRESH_TOKENS on, two parallel refreshes look like token reuse and
 * the backend signs the user out.
 *
 * Resolves null when there is no session (caller should stay disconnected).
 */
export function getFreshWsToken(injector: Injector, force = false): Promise<string | null> {
    const token = TokenStore.get();
    if (token && !force && !isJwtExpired(token)) {
        return Promise.resolve(token);
    }
    if (!TokenStore.isLoggedIn()) {
        return Promise.resolve(null);
    }
    const auth = injector.get(AuthSessionService, null);
    if (!auth) {
        return Promise.resolve(token);
    }
    return new Promise(resolve => {
        auth.refresh().subscribe({
            next: access => resolve(access || null),
            error: () => resolve(null), // TOKEN_REUSE_DETECTED - must sign in again
        });
    });
}
